import { isPhoneEmpty } from "Utils/PhoneUtils";
import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { usePartyQuery } from "Api/Queries/Party/usePartyQuery";
import { useMerchantFacilityInfo } from "Components/Shared/Merchant/Facilities/hooks/useMerchantFacilityInfo";
import type { MerchantCompleteRegistrationPageStepID } from "Components/Hunter/Merchants/CompleteRegistration/steps";
import { PartyType, RelationType } from "Infrastructure/Api/Api";

type CompletionStatus = Record<MerchantCompleteRegistrationPageStepID, boolean>;

type ReturnType = {
  isLoading: boolean;
  completionStatus: CompletionStatus | null;
};

export const useMerchantCompleteRegistrationPageStepsCompletionStatus = (
  partyPublicID: string,
  merchantPublicID: string,
): ReturnType => {
  const { data: partyData, isLoading: isPartyLoading } =
    usePartyQuery(partyPublicID);
  const { isRequired: requiresFacility, isLoading: isProductsLoading } =
    useMerchantFacilityInfo(merchantPublicID);
  const { data: merchantData, isLoading: isMerchantLoading } =
    useGetMerchantQuery(merchantPublicID);

  const isLoading = isPartyLoading || isProductsLoading || isMerchantLoading;

  if (isLoading) {
    return { isLoading, completionStatus: null };
  }

  if (!partyData) {
    return { isLoading: false, completionStatus: null };
  }

  const isContactPersonFilled = !!partyData?.relatedParties.some(
    ({ relationType }) => relationType === RelationType.ContactPerson,
  );

  const isSelfEmployed = partyData.type === PartyType.SelfEmployed;

  const completionStatus = {
    party: isSelfEmployed
      ? !!partyData?.email && !isPhoneEmpty(partyData.phone)
      : isContactPersonFilled,
    notes: true, //optional step
    products: true,
    facilities: !requiresFacility || (merchantData?.facilities.length ?? 0) > 0,
    completion: false,
  };

  return { isLoading: false, completionStatus };
};
